import { faBed, faMartiniGlassCitrus, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconButton } from '@mui/material'
import { Avatar, Button, Grid, Stack, Typography } from '@vapor/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BillSearchQuery, BillSearchQueryVariables, BillItemFragment, BillSearchDocument, BillCategoryType, BillItemReservedResourceFragment } from 'src/api/api.generated'
import QueryLookup from 'src/app/components/query-lookup/query-lookup'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import BillCreationModal from '../creation/bill-creation-modal'
import { useBillCreation } from '../creation/bill-creation.hook'
import { ReservationF } from '../../reservation-detail/types/reservation-detail.types'
import { compareDates, isDateBetween } from 'src/utils/date'
import { HicChip } from 'src/app/components/hic-chip/hic-chip'
import { getCheckinFromBill, getCheckoutFromBill } from './bills-list/pos-passerby-bills-list.hook'

export type BillLookupProps = {
  propertyId: string
  value: BillItemFragment | { id: string } | null
  onChange: (value: BillItemFragment | null) => void
  disabled?: boolean
  error?: boolean
  reservationIds?: string[]
  reservedResourceIds?: string[]
  defaultNewBillCategory?: BillCategoryType
  reservation?: ReservationF
  // The bill id will be removed from result of search
  billIdExcludedFromResult?: string
  disableNew?: boolean
  passerByOnly?: boolean
}

export default function BillLookup(props: BillLookupProps) {
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const { value, onChange, disabled, error, propertyId, reservation, passerByOnly = false } = props
  const formatDate = useFormatDate()
  const hotelDate = useHotelDate()

  //#region dialog
  const [creationDialogIsOpen, setCreationDialogIsOpen] = useState(false)
  const openCreationDialog = () => setCreationDialogIsOpen(true)
  const closeCreationDialog = () => setCreationDialogIsOpen(false)
  //#endregion dialog

  const { createNewBill, createNewBillLoading } = useBillCreation({
    propertyId: propertyId,
    onChange: onChange,
    closeCreationDialog: closeCreationDialog,
    reservationIds: props.reservationIds,
    reservedResourceIds: props.reservedResourceIds,
    defaultNewBillCategory: props.defaultNewBillCategory,
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(createNewBillLoading)
  }, [createNewBillLoading])

  useEffect(() => {
    setLoading(disabled ?? false)
  }, [disabled])

  const { md } = useBreakpoints()

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Grid container spacing={'8px'}>
          <Grid item xs>
            <QueryLookup<BillItemFragment, BillSearchQuery, BillSearchQueryVariables> //
              value={value ? value : null}
              onChange={onChange}
              queryDocument={BillSearchDocument}
              fetchQueryVariables={(id) => ({
                filter: {
                  id: { eq: id },
                  tenantId: {
                    eq: tenantId,
                  },
                  propertyId: {
                    eq: props.propertyId,
                  },
                  open: true,
                  isActive: true,
                  reservationId: passerByOnly
                    ? {
                        exists: false,
                      }
                    : undefined,
                },
                limit: 1,
              })}
              getFetchRecord={(data: BillSearchQuery) => data.bills?.[0] || null}
              searchQueryVariables={(text) => ({
                filter: {
                  description: { contains: text },
                  tenantId: {
                    eq: tenantId,
                  },
                  propertyId: {
                    eq: props.propertyId,
                  },
                  open: true,
                  isActive: true,
                  reservationId: passerByOnly
                    ? {
                        exists: false,
                      }
                    : undefined,
                },
              })}
              getSearchRecords={(data: BillSearchQuery) =>
                data.bills
                  ?.filter((b) => b.id !== props.billIdExcludedFromResult)
                  .filter((bill) => {
                    if (bill.reservedResource?.length) {
                      const checkin = bill.reservedResource ? getCheckinFromBill(bill) : bill.auditable.createdOn
                      const checkout = bill.reservedResource ? getCheckoutFromBill(bill) : bill.auditable.createdOn

                      if (!checkin || !checkout) return false

                      return isDateBetween(hotelDate, checkin, checkout)
                    } else {
                      return bill
                    }
                  })
                  .sort((firstBill, secondBill) => {
                    const firstBillDate = firstBill.reservedResource ? getCheckinFromBill(firstBill) : firstBill.auditable.createdOn
                    const secondBillDate = secondBill.reservedResource ? getCheckinFromBill(secondBill) : secondBill.auditable.createdOn

                    return firstBillDate && secondBillDate ? compareDates(secondBillDate, firstBillDate) : firstBillDate ? -1 : secondBillDate ? 1 : 0
                  }) || []
              }
              getOptionLabel={(option) => option.owner.name}
              disabled={loading}
              error={error}
              renderOption={(value) => {
                // const code =
                //   value.reservation
                //     ?.filter((res) => res !== null)
                //     ?.map((res) => res?.reservationCode ?? '')
                //     .reduce((acc, current) => acc + current, '') ?? ''

                const startDate: number[] = []
                const endDate: number[] = []

                value.reservedResource?.forEach((res) => {
                  if (res && res !== null) {
                    const dStart = res.start.getTime()
                    const dEnd = res.end.getTime()
                    if (dStart) startDate.push(dStart)
                    if (dEnd) endDate.push(dEnd)
                  }
                })

                const minDate = startDate.length ? formatDate(new Date(Math.min(...startDate))) : ''
                const maxDate = endDate.length ? formatDate(new Date(Math.max(...endDate))) : ''

                const createdOn = value.auditable.createdOn ?? new Date()

                const isPasserbyBill = (value.reservation ?? []).length > 0 ? false : true

                const isNoShow = value.reservation ? value.reservation.findIndex((r) => r?.reservationState?.noshow) >= 0 : false

                return (
                  <Stack direction="row" alignItems="center" spacing={1} width="100%" minWidth={{ xs: '100%', sm: '350px', md: '450px' }}>
                    <Avatar>
                      <FontAwesomeIcon icon={isPasserbyBill ? faMartiniGlassCitrus : faBed} style={{ width: '18px', height: '18px', color: 'var(--Cadet)' }} />
                    </Avatar>
                    <Stack key={value.id} direction="column" spacing={0.5}>
                      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <b>{`${value.description} - ${value.searchInfo?.ownerName}`}</b>
                        </Typography>
                        {value.searchInfo?.guestNames && value.searchInfo.guestNames.length > 0 ? (
                          <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {`(${value.searchInfo.guestNames.join(', ')})`}
                          </Typography>
                        ) : undefined}
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {!isPasserbyBill && value.category !== 'MAIN' && (
                          <Typography variant="caption" sx={{ background: 'var(--QuickSilver)', borderRadius: '3px', px: 0.5, color: 'white', maxHeight: '1rem' }}>
                            {value.category.toString()}
                          </Typography>
                        )}
                        <Typography variant="caption">{isPasserbyBill ? formatDate(createdOn) : `${minDate} > ${maxDate}`}</Typography>
                        {value.searchInfo?.resources && value.searchInfo.resources.length > 0 && (
                          <>
                            <FontAwesomeIcon icon={faBed} color="gray" />
                            <Typography variant="caption">{value.searchInfo.resources.join(', ')}</Typography>
                          </>
                        )}
                        {isNoShow && <HicChip borderColor="gray" text={t('pms.booking.planning.event-popover.phase.reservation-noshow')} textColor="orange" borderStyle="solid" />}
                      </Stack>
                    </Stack>
                  </Stack>
                )
              }}
              noOptionsText={<i>{t('commons.lookup.search')}</i>}
            />
          </Grid>
          {!props.disableNew && (
            <Grid item xs="auto">
              <Stack alignItems="center" direction="row" sx={{ height: '100%' }}>
                {md && (
                  <Button size="small" onClick={openCreationDialog} variant="outlined" sx={{ width: '150px !important' }} disabled={loading}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FontAwesomeIcon icon={faPlus} />
                      <p>{t('pms.booking.passerby-bill.list-action.add-new-bill')}</p>
                    </Stack>
                  </Button>
                )}
                {!md && (
                  <IconButton onClick={openCreationDialog}>
                    <FontAwesomeIcon icon={faPlus} />
                  </IconButton>
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      </Stack>
      <BillCreationModal
        open={creationDialogIsOpen}
        setOpen={(value) => (value ? openCreationDialog() : closeCreationDialog())}
        title={t('pms.booking.passerby-bill.creation-dialog.title-creation')}
        confirmAction={async (passerbyBill) => {
          createNewBill(passerbyBill)
        }}
        cancelAction={() => closeCreationDialog()}
        reservation={reservation}
      />
    </>
  )
}
